import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsent } from './ConsentContext';
import { CONSENT_CATEGORIES } from './privacyConfig';
import { emptyChoices } from './consent';
import './privacy.css';

const isSnapshot = () => typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

/* ─── Banner ────────────────────────────────────────────────────────────── */

// Shown until the visitor answers. Accept, Reject and Manage carry equal
// weight: same size, same style, same row.
export const ConsentBanner = () => {
  const { loaded, decided, acceptAll, rejectAll, openPreferences, prefsOpen } = useConsent();
  if (!loaded || decided || prefsOpen || isSnapshot()) return null;
  return (
    <section className="cc-banner" aria-label="Privacy choices" aria-live="polite">
      <div className="cc-banner-inner">
        <div className="cc-banner-text">
          <h2 className="cc-banner-title">Your privacy choices</h2>
          <p>
            This site uses only what it needs to work: keeping you signed in and remembering this choice.
            With your permission we would also load Google reCAPTCHA on the sign-up and password-reset
            forms. Nothing else is tracked. You can change your mind any time from “Privacy choices” in the
            footer. <Link to="/privacy-policy#cookies">Read the cookie notice</Link>.
          </p>
        </div>
        <div className="cc-actions">
          <button type="button" className="cc-btn" onClick={acceptAll}>Accept all</button>
          <button type="button" className="cc-btn" onClick={rejectAll}>Reject all</button>
          <button type="button" className="cc-btn" onClick={openPreferences}>Manage choices</button>
        </div>
      </div>
    </section>
  );
};

/* ─── Preferences dialog ────────────────────────────────────────────────── */

export const ConsentPreferences = () => {
  const { prefsOpen, closePreferences, record, saveChoices, acceptAll, rejectAll } = useConsent();
  const [choices, setChoices] = useState(emptyChoices());
  const dialogRef = useRef(null);
  const returnFocus = useRef(null);

  // Start from what was chosen before — never pre-ticked otherwise.
  useEffect(() => {
    if (!prefsOpen) return undefined;
    setChoices({ ...emptyChoices(), ...(record ? record.categories : {}) });
    returnFocus.current = document.activeElement;
    const t = setTimeout(() => dialogRef.current && dialogRef.current.querySelector('input:not([disabled]), button').focus(), 0);
    const onKey = (e) => { if (e.key === 'Escape') closePreferences(); };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      if (returnFocus.current && returnFocus.current.focus) returnFocus.current.focus();
    };
  }, [prefsOpen, record, closePreferences]);

  if (!prefsOpen) return null;

  // Keep Tab inside the dialog.
  const trap = (e) => {
    if (e.key !== 'Tab') return;
    const f = Array.from(dialogRef.current.querySelectorAll('button, input:not([disabled]), a[href]'));
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return (
    <div className="cc-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) closePreferences(); }}>
      <div className="cc-dialog" role="dialog" aria-modal="true" aria-labelledby="cc-dialog-title" ref={dialogRef} onKeyDown={trap}>
        <div className="cc-dialog-head">
          <h2 id="cc-dialog-title">Privacy choices</h2>
          <button type="button" className="cc-close" onClick={closePreferences} aria-label="Close privacy choices">×</button>
        </div>
        <p className="cc-dialog-lead">
          Choose what this website may load. Strictly necessary items are always on. Everything else stays
          off unless you switch it on, and you can come back and change it at any time.
        </p>
        <ul className="cc-cats">
          {CONSENT_CATEGORIES.map((c) => {
            const id = `cc-cat-${c.key}`;
            const on = c.required ? true : Boolean(choices[c.key]);
            return (
              <li key={c.key} className="cc-cat">
                <div className="cc-cat-head">
                  <label htmlFor={id} className="cc-cat-label">{c.label}</label>
                  <span className="cc-switch">
                    <input
                      id={id}
                      type="checkbox"
                      role="switch"
                      checked={on}
                      disabled={c.required}
                      aria-describedby={`${id}-desc`}
                      onChange={(e) => setChoices({ ...choices, [c.key]: e.target.checked })}
                    />
                    <span aria-hidden="true" />
                  </span>
                </div>
                <p id={`${id}-desc`} className="cc-cat-desc">{c.required ? `${c.desc} Always on.` : c.desc}</p>
              </li>
            );
          })}
        </ul>
        <div className="cc-actions cc-actions--dialog">
          <button type="button" className="cc-btn" onClick={() => saveChoices(choices)}>Save choices</button>
          <button type="button" className="cc-btn" onClick={acceptAll}>Accept all</button>
          <button type="button" className="cc-btn" onClick={rejectAll}>Reject all</button>
        </div>
        {record && (
          <p className="cc-record">
            Your current choice was saved on {new Date(record.timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })} (notice version {record.noticeVersion}).
          </p>
        )}
      </div>
    </div>
  );
};

/* ─── Gate for an optional third-party script on one form ───────────────── */

// Renders its children (e.g. <ReCAPTCHA />) only once the visitor has said
// yes to `category`. Until then, a notice explains what would load and why.
export const ConsentGate = ({ category, children, title, what }) => {
  const { has, grant } = useConsent();
  if (has(category)) return children;
  return (
    <div className="cc-gate" role="group" aria-label={title}>
      <p className="cc-gate-title">{title}</p>
      <p className="cc-gate-text">{what}</p>
      <div className="cc-gate-actions">
        <button type="button" className="cc-btn cc-btn--small" onClick={() => grant(category)}>Allow and continue</button>
        <Link to="/privacy-policy#cookies" className="cc-gate-link">What this means</Link>
      </div>
    </div>
  );
};

/* ─── Consent notice + checkbox for a form ──────────────────────────────── */

// Plain-language notice: purpose, data, retention and the right to withdraw,
// with an unchecked box the form must not submit without.
export const FormConsent = ({ id, checked, onChange, purpose, data, retention, error }) => (
  <div className={`cc-form ${error ? 'has-error' : ''}`}>
    <p className="cc-form-notice">
      <strong>How we use this:</strong> {purpose} We collect {data}, and keep it {retention}. You can
      withdraw consent or ask us to delete it at any time from our{' '}
      <Link to="/privacy-request">data request page</Link>. See the <Link to="/privacy-policy#privacy">privacy notice</Link>.
    </p>
    <label className="cc-form-check" htmlFor={id}>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} required aria-invalid={error ? 'true' : undefined} />
      <span>I agree to {SHORT_AGREE}</span>
    </label>
    {error && <p className="cc-form-error" role="alert">Please tick the box to agree before sending.</p>}
  </div>
);

const SHORT_AGREE = 'Raya using these details for the purpose above.';
